import { COLORS } from '@repo/design-system/index'
import { QIcon_chevron_down, QIcon_chevron_up } from '@repo/icons/index'
import { useEffect, useRef, useState } from 'react'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import { checkIsSelected } from '../useLandingNav'

interface DropdownProps {
  navTitle: string
  subTitle?: string
  path?: string
  children?: React.ReactNode | ((onClose: () => void) => React.ReactNode)
}

const MainNavDropdown = ({ navTitle, subTitle = '', path = null, children }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const isMobile = useIsMobile()
  const router = useAppRouter()

  const hasTag = 'tag' in router.query
  const isSelectedMenu = checkIsSelected(router, path)

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen((prev) => !prev)
  }

  useEffect(() => {
    if (isMobile) return

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const closeDropdown = () => setIsOpen(false)

  return (
    <div className="relative" ref={dropdownRef}>
      <button className="flex w-full items-center justify-between" onClick={handleButtonClick}>
        <div className="flex gap-x-8 sm:gap-x-4 items-center">
          <span
            className={`Body1-16Bd sm:Body2-14Md !font-[400] ${
              isSelectedMenu && !hasTag ? 'text-purple-500' : 'sm:text-gray-500'
            }`}
          >
            {navTitle}
          </span>
          {isOpen ? (
            <QIcon_chevron_up width={16} height={16} color={COLORS.GRAY500} />
          ) : (
            <QIcon_chevron_down width={16} height={16} color={COLORS.GRAY500} />
          )}
        </div>
        {subTitle && <span className="Body2-14Md text-gray-400">{subTitle}</span>}
      </button>

      <div
        className={`sm:absolute sm:top-36 bg-white rounded-lg sm:z-[10000] sm:shadow-[0px_2px_8px_0px_rgba(0,0,0,0.15)] w-auto min-w-max overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-screen opacity-100 scale-y-100' : 'max-h-0 opacity-0 scale-y-95'
        }`}
      >
        {typeof children === 'function' ? children(closeDropdown) : children}
      </div>
    </div>
  )
}

export default MainNavDropdown
